/**
 * PrescreenRunner — run a study's published prescreen live on a call.
 *
 * Speed-first: one question per screen, large answer targets, keyboard 1-9
 * selects a choice (1/2 = Yes/No) and Enter advances. The attempt lifecycle and
 * optimistic answer-save live in usePrescreenAttempt (shared with the all-visible
 * Call Mode pane); this component owns only the one-at-a-time navigation, the
 * keyboard shortcuts, and the finish summary. A failing criterion shows an inline
 * banner with a one-tap Disqualify path; the finish screen summarizes
 * pass/fail/unknown with Mark-eligible / Disqualify CTAs.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, BookOpen, Check, ChevronLeft, ChevronRight, Loader2, X } from 'lucide-react';
import api from '../../../services/api';
import { PrescreenCriterion, studyInternalLabel } from '../../types';
import { notifyStatsChanged, describeFailingAnswer } from '../../lib';
import { usePromptDialog } from '../ui';
import FormRenderer from '../forms/FormRenderer';
import StudyReferenceDrawer from '../studies/StudyReferenceDrawer';
import DisqualifyModal from './DisqualifyModal';
import CalcReadout from './CalcReadout';
import { usePrescreenAttempt } from './usePrescreenAttempt';
import RandomizedConflictModal from '../RandomizedConflictModal';

interface PrescreenRunnerProps {
  contactId: string;
  study: { id: string; title: string };
  /** 'stepper' (default) walks one question at a time with 1-9/Enter shortcuts;
   *  'list' shows every question on one tabbable surface (the contact rail). */
  layout?: 'stepper' | 'list';
  onClose: () => void;
  /** Called after any terminal action (eligible / disqualified) so the parent refetches. */
  onFinished: () => void;
  /** Phase F: a pivot prescreen started from the DQ modal — the parent swaps
   *  in a fresh runner (keyed remount), which also unmounts this one. */
  onPivot?: (study: { id: string; title: string }) => void;
}

export default function PrescreenRunner({ contactId, study, layout = 'stepper', onClose, onFinished, onPivot }: PrescreenRunnerProps) {
  const ps = usePrescreenAttempt(contactId, study.id);
  const prompt = usePromptDialog();
  const [view, setView] = useState<'running' | 'summary'>('running');
  const [idx, setIdx] = useState(0);
  const [dqFor, setDqFor] = useState<PrescreenCriterion | null | 'summary'>(null);
  const [refOpen, setRefOpen] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Render/tally over the VISIBLE list — questions hidden by a show_if
  // condition are skipped and never count against (or for) the patient.
  // Answering a gating question re-shapes this list live; clamping the index
  // keeps the stepper on a real question when later ones hide.
  const criteria = ps.visibleCriteria;
  const current = criteria.length ? criteria[Math.min(idx, criteria.length - 1)] : null;

  // Jump to the first unanswered question once the attempt is ready (resumed runs).
  useEffect(() => {
    if (ps.phase === 'ready') setIdx(ps.initialIdx);
  }, [ps.phase, ps.initialIdx]);

  const next = useCallback(() => {
    setIdx((i) => {
      if (i >= criteria.length - 1) {
        setView('summary');
        return i;
      }
      return i + 1;
    });
  }, [criteria.length]);

  // Keyboard shortcuts are SCOPED to the runner container (events bubbling
  // from inside it) — a window listener would hijack Enter/digits from the
  // Composer and every other control on ContactDetail while a call is live.
  const onRunnerKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (layout === 'list') return; // list mode tabs through native inputs — no digit/Enter capture
    if (ps.phase !== 'ready' || view !== 'running' || !current || dqFor || refOpen) return;
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    const target = e.target as HTMLElement;
    if (target.tagName === 'BUTTON' || target.tagName === 'A') return; // native activation wins
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') {
      if (e.key === 'Enter' && current.answer_type !== 'text') { e.preventDefault(); next(); }
      return;
    }
    if (e.key === 'Enter') { e.preventDefault(); next(); return; }
    if (current.calculation) return; // computed rows aren't answered — Enter only
    const n = Number(e.key);
    if (!Number.isInteger(n) || n < 1 || n > 9) return;
    if (current.answer_type === 'boolean') {
      if (n === 1) ps.setAnswer(current, true);
      if (n === 2) ps.setAnswer(current, false);
    } else if (current.answer_type === 'choice') {
      const opt = (current.options || [])[n - 1];
      if (opt) ps.setAnswer(current, opt.value);
    }
  }, [layout, ps, view, current, dqFor, refOpen, next]);

  // The DQ modal (z-[70]) renders ABOVE the drawer (z-40) and traps nothing —
  // an open drawer underneath would keep stealing focus and hijacking
  // Escape/Tab into invisible UI, so it closes whenever the modal opens.
  useEffect(() => {
    if (dqFor) setRefOpen(false);
  }, [dqFor]);

  // Keep the container focused as questions advance so keys land in scope.
  // List mode relies on native tab order, so it never grabs focus.
  useEffect(() => {
    if (layout === 'list' || ps.phase !== 'ready' || view !== 'running' || dqFor || refOpen) return;
    const active = document.activeElement as HTMLElement | null;
    if (!containerRef.current || (active && containerRef.current.contains(active) && active !== containerRef.current)) return;
    containerRef.current.focus();
  }, [layout, ps.phase, view, idx, dqFor, refOpen]);

  const failing = criteria.filter((c) => ps.verdictFor(c) === 'fail');
  const unknownCount = criteria.filter((c) => ps.verdictFor(c) === 'unknown').length;
  const passCount = criteria.length - failing.length - unknownCount;

  // The actual failing answer(s) — pre-fills the DQ "Details" so the recorded
  // reason captures the disqualifying VALUE, not just the question.
  const failingDetails = failing.map((c) => describeFailingAnswer(c, ps.resolvedAnswers[c.question_key])).join('\n');

  // Mark eligible. With failing criteria present, `override` is required — the
  // recruiter confirms (and may note why); the override is recorded server-side.
  const markEligible = async (override = false) => {
    if (!ps.attempt) return;
    let overrideReason: string | null = null;
    if (override) {
      const r = await prompt({
        title: `Mark this patient ELIGIBLE despite ${failing.length} criteria that did not meet?`,
        message: 'This overrides the prescreen result and is recorded on the timeline.',
        label: 'Optionally note why',
        initialValue: '',
        confirmLabel: 'Mark eligible',
        danger: true,
      });
      if (r === null) return; // cancelled
      overrideReason = r.trim() || null;
    }
    setFinishing(true);
    try {
      await ps.flushPending(); // the attempt must not close ahead of its answers
      await api.patch(`/api/crm/attempts/${ps.attempt.id}`, {
        status: 'eligible',
        ...(override ? { override: true, override_reason: overrideReason, override_criteria: failing.map((c) => c.display_question) } : {}),
      });
      notifyStatsChanged();
      onFinished();
      onClose();
    } catch (err: any) {
      ps.setErrorMsg(err?.response?.data?.error || 'Could not mark eligible');
      setFinishing(false);
    }
  };

  const card = 'rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800';

  // The terminal CTAs (record a DQ, or pass / override-pass) — shared verbatim by
  // the stepper's summary screen and the list layout's footer.
  const terminalActions = (
    <>
      <button
        type="button"
        onClick={() => { ps.flushPending(); setDqFor(failing[0] || 'summary'); }}
        disabled={finishing}
        className="rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-red-700 disabled:opacity-50"
      >
        Disqualify…
      </button>
      {failing.length > 0 ? (
        <button
          type="button"
          onClick={() => markEligible(true)}
          disabled={finishing}
          title="Override the screener and mark this patient eligible anyway (recorded)"
          className="inline-flex items-center gap-1.5 rounded-lg border border-amber-400 bg-amber-50 px-3 py-1.5 text-sm font-medium text-amber-800 transition-colors duration-150 hover:bg-amber-100 disabled:opacity-50 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-200 dark:hover:bg-amber-900/50"
        >
          <Check className="h-4 w-4" /> {finishing ? 'Saving…' : 'Override: mark eligible'}
        </button>
      ) : (
        <button
          type="button"
          onClick={() => markEligible(false)}
          disabled={finishing}
          className="inline-flex items-center gap-1.5 rounded-lg bg-green-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-green-700 disabled:opacity-50"
        >
          <Check className="h-4 w-4" /> {finishing ? 'Saving…' : 'Mark eligible'}
        </button>
      )}
    </>
  );

  if (ps.phase === 'starting') {
    return (
      <div className={`${card} flex items-center gap-3 p-4`}>
        <Loader2 className="h-4 w-4 animate-spin text-primary-600" />
        <span className="text-sm text-gray-600 dark:text-gray-300">Starting prescreen for {studyInternalLabel(study)}…</span>
      </div>
    );
  }
  if (ps.phase === 'no_prescreen' || ps.phase === 'error') {
    return (
      <div className={`${card} p-4`}>
        {/* The attempt create was blocked because the lead is randomized in
            another study — acknowledging re-runs the start (plan §5.2 site 4).
            The card below stays the exit if the recruiter declines. */}
        {ps.conflict && (
          <RandomizedConflictModal
            targetStudyTitle={studyInternalLabel(study)}
            randomizedStudyTitle={ps.conflict.randomizedStudyTitle}
            randomizedAt={ps.conflict.randomizedAt}
            onCancel={ps.dismissConflict}
            onConfirm={ps.acknowledgeConflict}
          />
        )}
        <p className="text-sm text-gray-700 dark:text-gray-300">
          {ps.phase === 'no_prescreen'
            ? `${studyInternalLabel(study)} has no published prescreen yet — an admin can create one in Studies.`
            : ps.errorMsg}
        </p>
        <button type="button" onClick={onClose} className="mt-3 text-sm font-medium text-primary-700 hover:text-primary-800 dark:text-primary-400">
          Close
        </button>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      tabIndex={-1}
      onKeyDown={onRunnerKeyDown}
      className={`${card} focus:outline-none`}
    >
      {/* Sticky header: study + progress + close */}
      <div className="flex items-center justify-between gap-2 border-b border-gray-200 px-4 py-2.5 dark:border-gray-700">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">Prescreen · {studyInternalLabel(study)}</p>
          {layout === 'stepper' && view === 'running' && (
            <p className="text-xs text-gray-400">Question {idx + 1} of {criteria.length}</p>
          )}
          {layout === 'list' && (
            <p className="text-xs text-gray-400">{criteria.length} question{criteria.length === 1 ? '' : 's'} · answer in any order</p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={() => { if (!dqFor) setRefOpen(true); }}
            aria-label="Open the study reference"
            title="Study reference: summary, recruiter script, stipend"
            className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg p-2 text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 sm:min-h-0 sm:min-w-0"
          >
            <BookOpen className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close prescreen (progress is saved)"
            title="Close — progress is saved"
            className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg p-2 text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 sm:min-h-0 sm:min-w-0"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* List layout: every question on one tabbable surface (contact rail). */}
      {layout === 'list' && (
        <div className="p-4">
          <div className="space-y-2">
            {criteria.map((c, i) => {
              const verdict = ps.verdictFor(c);
              return (
                <div
                  key={c.id}
                  className={`rounded-xl border p-2.5 ${
                    verdict === 'fail'
                      ? 'border-red-300 bg-red-50/50 dark:border-red-800 dark:bg-red-900/10'
                      : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'
                  }`}
                >
                  <div className="mb-1.5 flex items-start justify-between gap-2">
                    <span className="text-xs font-medium text-gray-400">{i + 1}. {c.criterion_kind}</span>
                    {verdict === 'fail' && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-medium text-red-700 dark:bg-red-900/30 dark:text-red-300">
                        <AlertTriangle className="h-3 w-3" /> Does not meet
                      </span>
                    )}
                    {verdict === 'pass' && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-medium text-green-700 dark:bg-green-900/30 dark:text-green-300">
                        <Check className="h-3 w-3" /> OK
                      </span>
                    )}
                  </div>
                  {c.calculation ? (
                    <CalcReadout label={c.display_question} value={ps.resolvedAnswers[c.question_key]} verdict={verdict} />
                  ) : (
                    <FormRenderer
                      mode="single"
                      dense
                      questions={[{
                        key: c.question_key,
                        label: c.display_question,
                        type: c.answer_type,
                        options: c.options,
                      }]}
                      answers={{ [c.question_key]: ps.answers[c.question_key] }}
                      onAnswer={(_, value) => ps.setAnswer(c, value)}
                    />
                  )}
                  {verdict === 'fail' && (
                    <div className="mt-2 flex justify-end">
                      <button
                        type="button"
                        onClick={() => { ps.flushPending(); setDqFor(c); }}
                        className="rounded-lg bg-red-600 px-3 py-1 text-xs font-medium text-white transition-colors duration-150 hover:bg-red-700"
                      >
                        Disqualify…
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Footer: live tally + the same terminal actions as the stepper summary. */}
          <div className="mt-4 border-t border-gray-200 pt-3 dark:border-gray-700">
            <div className="mb-2 flex items-center justify-center gap-3 text-xs">
              <span className="text-green-600 dark:text-green-400">{passCount} pass</span>
              <span className="text-red-600 dark:text-red-400">{failing.length} fail</span>
              <span className="text-gray-500">{unknownCount} judgment</span>
            </div>
            {ps.errorMsg && <p className="mb-2 text-center text-sm text-red-600 dark:text-red-400">{ps.errorMsg}</p>}
            <div className="flex flex-wrap justify-end gap-2">{terminalActions}</div>
            <p className="mt-2 text-center text-[11px] text-gray-400">Tab between answers · progress saves automatically</p>
          </div>
        </div>
      )}

      {layout === 'stepper' && view === 'running' && current && (
        <div className="p-4">
          {/* progress bar */}
          <div className="mb-4 h-1 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
            <div className="h-full bg-primary-500 transition-all" style={{ width: `${(idx / Math.max(criteria.length, 1)) * 100}%` }} />
          </div>

          {current.calculation ? (
            <CalcReadout label={current.display_question} value={ps.resolvedAnswers[current.question_key]} verdict={ps.verdictFor(current)} large />
          ) : (
            <FormRenderer
              mode="single"
              questions={[{
                key: current.question_key,
                label: current.display_question,
                type: current.answer_type,
                options: current.options,
              }]}
              answers={{ [current.question_key]: ps.answers[current.question_key] }}
              onAnswer={(_, value) => ps.setAnswer(current, value)}
            />
          )}

          {ps.verdictFor(current) === 'fail' && (
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/20">
              <p className="inline-flex items-center gap-1.5 text-sm font-medium text-red-700 dark:text-red-300">
                <AlertTriangle className="h-4 w-4" /> This answer does not meet the {current.criterion_kind} criterion
              </p>
              <button
                type="button"
                onClick={() => { ps.flushPending(); setDqFor(current); }}
                className="rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-red-700"
              >
                Disqualify…
              </button>
            </div>
          )}

          <div className="mt-4 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setIdx((i) => Math.max(0, i - 1))}
              disabled={idx === 0}
              className="inline-flex min-h-[44px] items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-50 disabled:opacity-40 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 sm:min-h-0"
            >
              <ChevronLeft className="h-4 w-4" /> Back
            </button>
            <button
              type="button"
              onClick={next}
              className="inline-flex min-h-[44px] items-center gap-1 rounded-lg bg-primary-600 px-4 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 sm:min-h-0"
            >
              {idx >= criteria.length - 1 ? 'Finish' : 'Next'} <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <p className="mt-2 text-center text-[11px] text-gray-400">Keys 1–9 answer · Enter continues · progress saves automatically</p>
        </div>
      )}

      {layout === 'stepper' && view === 'summary' && (
        <div className="p-4">
          <div className="mb-3 grid grid-cols-3 gap-2 text-center">
            <div className="rounded-lg bg-green-50 p-2 dark:bg-green-900/20">
              <p className="text-lg font-semibold text-green-700 dark:text-green-300">{passCount}</p>
              <p className="text-xs text-green-700/80 dark:text-green-300/80">Pass</p>
            </div>
            <div className="rounded-lg bg-red-50 p-2 dark:bg-red-900/20">
              <p className="text-lg font-semibold text-red-700 dark:text-red-300">{failing.length}</p>
              <p className="text-xs text-red-700/80 dark:text-red-300/80">Fail</p>
            </div>
            <div className="rounded-lg bg-gray-50 p-2 dark:bg-gray-700/40">
              <p className="text-lg font-semibold text-gray-700 dark:text-gray-200">{unknownCount}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Judgment</p>
            </div>
          </div>

          {failing.length > 0 && (
            <ul className="mb-3 space-y-1">
              {failing.map((c) => (
                <li key={c.id} className="flex items-start gap-1.5 text-sm text-red-700 dark:text-red-300">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                  <span className="min-w-0">{c.display_question}</span>
                </li>
              ))}
            </ul>
          )}
          {ps.errorMsg && <p className="mb-2 text-sm text-red-600 dark:text-red-400">{ps.errorMsg}</p>}

          <div className="flex flex-wrap justify-end gap-2">
            <button
              type="button"
              onClick={() => setView('running')}
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              Review answers
            </button>
            {terminalActions}
          </div>
        </div>
      )}

      <StudyReferenceDrawer
        open={refOpen}
        onClose={() => setRefOpen(false)}
        initialStudyId={study.id}
      />

      {dqFor && ps.attempt && (
        <DisqualifyModal
          contactId={contactId}
          study={study}
          attemptId={ps.attempt.id}
          criteria={criteria}
          preselectedCriterion={dqFor === 'summary' ? null : dqFor}
          prefillDetails={failingDetails}
          onClose={() => setDqFor(null)}
          onRecorded={() => {
            setDqFor(null);
            notifyStatsChanged();
            onFinished();
            onClose();
          }}
          onPivot={onPivot}
        />
      )}
    </div>
  );
}
